import { useCallback, useState } from "react"
import type { ChangeEvent } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { AlertTriangle, CheckCircle2, ClipboardList } from "lucide-react"
import { getPublicSurvey, submitPublicSurvey } from "@/services/surveys"
import { useApiMutation } from "@/hooks/use-api-mutation"
import { cn } from "@/lib/utils"
import { extractErrorCode, translateError } from "@/lib/error-messages"
import {
  SURVEY_OTHER_TEXT_MAX_CHARS,
  buildSurveyAnswers,
  isAnswerable,
  missingRequiredQuestions,
  otherOptionChosen,
  scalePoints,
  surveyFieldErrorMessage,
  surveyFieldErrors,
  textLengthBounds,
  type SurveyAnswerValue,
  type SurveyAnswers,
  type SurveyOtherTexts,
} from "@/lib/public-survey"
import type { PublicSurveyInfo, PublicSurveyQuestion } from "@/types/api"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

/**
 * Questionnaire de satisfaction public, ouvert depuis le lien de l'email
 * d'enquête (`/survey/:token`, sans compte). Les réponses alimentent la
 * satisfaction du bilan de session, des statistiques et l'indicateur 2.
 */
/** Seule une notice https est liée, comme côté serveur. */
const PRIVACY_NOTICE_SCHEME = "https://"

/**
 * Où le stagiaire s'informe sur le traitement de ses réponses.
 * Avec une notice : le lien vers elle.
 * Sans notice : le contact de l'organisme et la CNIL.
 */
function PrivacyInformation({ survey }: { survey: PublicSurveyInfo }) {
  return pipe(
    O.fromNullable(survey.privacy_notice_url),
    O.filter((url) => url.startsWith(PRIVACY_NOTICE_SCHEME)),
    O.match(
      () => (
        <div className="mt-2 space-y-1 text-sm text-muted-foreground" data-test="public-survey-privacy-contact">
          <p>
            Pour en savoir plus sur le traitement de vos données ou exercer vos droits, contactez{" "}
            {survey.organization_name}
            {pipe(
              O.fromNullable(survey.public_contact_email),
              O.match(
                () => null,
                (email) => (
                  <>
                    {" "}
                    à <a className="underline focus:outline-none focus:ring-2 focus:ring-ring" href={`mailto:${email}`}>{email}</a>
                  </>
                ),
              ),
            )}
            .
          </p>
          <p>
            Vous pouvez aussi introduire une réclamation auprès de la CNIL :{" "}
            <a
              className="underline focus:outline-none focus:ring-2 focus:ring-ring"
              href="https://www.cnil.fr"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.cnil.fr
              <span className="sr-only"> (s'ouvre dans un nouvel onglet)</span>
            </a>
            .
          </p>
        </div>
      ),
      (url) => (
        <a
          className="mt-2 inline-block text-sm underline focus:outline-none focus:ring-2 focus:ring-ring"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-test="public-survey-privacy-notice"
        >
          Informations sur le traitement de vos données
          <span className="sr-only"> (s'ouvre dans un nouvel onglet)</span>
        </a>
      ),
    ),
  )
}

/** Hauteur, en lignes, du champ d'une réponse longue. */
const TEXT_LONG_ROWS = 4

export default function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>()
  const surveyToken = pipe(
    O.fromNullable(token),
    O.getOrElse(() => ""),
  )
  const [answers, setAnswers] = useState<SurveyAnswers>({})
  const [others, setOthers] = useState<SurveyOtherTexts>({})
  const [submitted, setSubmitted] = useState(false)

  const surveyQuery = useQuery({
    queryKey: ["public-survey", surveyToken],
    queryFn: () => getPublicSurvey(surveyToken),
    enabled: surveyToken.length > 0,
    retry: false,
  })

  // Aucune liste de l'application à rafraîchir : `entity` vide. Le refus
  // s'affiche sous le formulaire, pas en toast.
  const submitMutation = useApiMutation(
    [],
    (questions: ReadonlyArray<PublicSurveyQuestion>) =>
      submitPublicSurvey(surveyToken, { answers: buildSurveyAnswers(questions, answers, others) }),
    {
      errorToast: false,
      onSuccess: () => setSubmitted(true),
    },
  )

  const setAnswer = (questionId: string, value: SurveyAnswerValue) =>
    setAnswers((previous) => ({ ...previous, [questionId]: value }))
  const setOther = useCallback(
    (questionId: string, text: string) => setOthers((previous) => ({ ...previous, [questionId]: text })),
    [],
  )
  // Questions désignées par le dernier refus du serveur (422 SURVEY_ANSWERS_INVALID).
  const fieldErrors = submitMutation.isError ? surveyFieldErrors(submitMutation.error) : {}

  if (surveyToken.length === 0 || surveyQuery.isError) {
    return (
      <SurveyShell>
        <UnavailableState error={surveyQuery.error} />
      </SurveyShell>
    )
  }

  if (submitted) {
    return (
      <SurveyShell>
        <div className="mx-auto mt-12 max-w-md p-8 text-center" data-test="public-survey-thanks">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" aria-hidden="true" />
          <h2 className="mt-3 text-lg font-semibold">Merci pour vos réponses</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground" data-test="public-survey-thanks-message">
            {pipe(
              O.fromNullable(surveyQuery.data),
              O.chain((survey) => O.fromNullable(survey.thank_you_message)),
              O.getOrElse(() => "Elles sont transmises à votre organisme de formation."),
            )}
          </p>
        </div>
      </SurveyShell>
    )
  }

  return (
    <SurveyShell>
      {pipe(
        O.fromNullable(surveyQuery.data),
        O.match(
          () => (
            <p className="p-8 text-center text-muted-foreground" data-test="public-survey-loading">
              Chargement du questionnaire…
            </p>
          ),
          (survey) => {
            const missing = missingRequiredQuestions(survey.questions, answers)
            return (
              <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-8" data-test="public-survey-page">
                <div>
                  <h1 className="text-2xl font-semibold" data-test="public-survey-title">
                    {survey.template_name}
                  </h1>
                  <p className="mt-2 text-sm text-muted-foreground" data-test="public-survey-organization">
                    Questionnaire de {survey.organization_name}, responsable du traitement de vos réponses. Vos réponses
                    sont nominatives : {survey.organization_name} les associe à votre nom.
                  </p>
                  <PrivacyInformation survey={survey} />
                  {survey.description && <p className="mt-2 text-muted-foreground">{survey.description}</p>}
                  {pipe(
                    O.fromNullable(survey.estimated_duration_minutes),
                    O.match(
                      () => null,
                      (minutes) => <p className="mt-1 text-sm text-muted-foreground">Durée estimée : {minutes} min</p>,
                    ),
                  )}
                </div>
                {survey.questions.map((question, index) => (
                  <QuestionCard
                    key={question.id}
                    index={index + 1}
                    question={question}
                    answer={O.fromNullable(answers[question.id])}
                    onAnswer={(value) => setAnswer(question.id, value)}
                    other={pipe(
                      O.fromNullable(others[question.id]),
                      O.getOrElse(() => ""),
                    )}
                    onOther={setOther}
                    error={O.fromNullable(fieldErrors[question.id])}
                  />
                ))}
                {submitMutation.isError && (
                  <Alert variant="destructive" data-test="public-survey-error">
                    <AlertDescription>{translateError(submitMutation.error)}</AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col items-end gap-2">
                  {missing.length > 0 && (
                    <p className="text-sm text-muted-foreground" data-test="public-survey-missing">
                      {missing.length} question(s) obligatoire(s) sans réponse.
                    </p>
                  )}
                  <Button
                    onClick={() => submitMutation.mutate(survey.questions)}
                    disabled={missing.length > 0 || submitMutation.isPending}
                    data-test="public-survey-submit"
                  >
                    {submitMutation.isPending ? "Envoi…" : "Envoyer mes réponses"}
                  </Button>
                </div>
              </div>
            )
          },
        ),
      )}
    </SurveyShell>
  )
}

function SurveyShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-2 p-4 text-base font-medium">
          <ClipboardList className="h-5 w-5 text-primary" aria-hidden="true" />
          Questionnaire de satisfaction
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}

/** Lien inconnu, expiré (410) ou déjà rempli (422) : le motif, jamais une page blanche. */
function UnavailableState({ error }: { error: unknown }) {
  const answered = extractErrorCode(error) === "SURVEY_ALREADY_COMPLETED"
  return (
    <div className="mx-auto mt-12 max-w-md p-8 text-center" data-test="public-survey-unavailable">
      {answered ? (
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" aria-hidden="true" />
      ) : (
        <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" aria-hidden="true" />
      )}
      <h2 className="mt-3 text-lg font-semibold">{answered ? "Questionnaire déjà rempli" : "Lien indisponible"}</h2>
      <p className="mt-2 text-sm text-muted-foreground" data-test="public-survey-unavailable-reason">
        {error ? translateError(error) : "Ce lien de questionnaire est incomplet."}
      </p>
    </div>
  )
}

function QuestionCard({
  index,
  question,
  answer,
  onAnswer,
  other,
  onOther,
  error,
}: {
  index: number
  question: PublicSurveyQuestion
  answer: O.Option<SurveyAnswerValue>
  onAnswer: (value: SurveyAnswerValue) => void
  other: string
  onOther: (questionId: string, text: string) => void
  /** Motif du refus de cette réponse par le serveur. */
  error: O.Option<string>
}) {
  const inputId = `survey-question-${question.id}`
  const changeOther = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onOther(question.id, event.target.value),
    [onOther, question.id],
  )
  return (
    <Card data-test={`public-survey-question-${index}`} className={cn(O.isSome(error) && "border-destructive")}>
      <CardHeader>
        <CardTitle className="text-base">
          {index}. {question.question_text}
          {question.is_required && isAnswerable(question) && (
            <span className="ml-1 text-destructive" aria-label="obligatoire">
              *
            </span>
          )}
        </CardTitle>
        {question.help_text && <CardDescription>{question.help_text}</CardDescription>}
      </CardHeader>
      <CardContent>
        <QuestionInput inputId={inputId} question={question} answer={answer} onAnswer={onAnswer} />
        {otherOptionChosen(question, answer) ? (
          <div className="mt-3 space-y-1">
            <Label htmlFor={`${inputId}-other`}>Précisez</Label>
            <Input
              id={`${inputId}-other`}
              value={other}
              maxLength={SURVEY_OTHER_TEXT_MAX_CHARS}
              onChange={changeOther}
              data-test={`${inputId}-other`}
            />
          </div>
        ) : null}
        {pipe(
          error,
          O.match(
            () => null,
            (reason) => (
              <p className="mt-2 text-sm text-destructive" role="alert" data-test={`${inputId}-error`}>
                {surveyFieldErrorMessage(reason)}
              </p>
            ),
          ),
        )}
      </CardContent>
    </Card>
  )
}

function textOf(answer: O.Option<SurveyAnswerValue>): string {
  return pipe(
    answer,
    O.chain((value) => (value.kind === "text" ? O.some(value.value) : O.none)),
    O.getOrElse(() => ""),
  )
}

function choicesOf(answer: O.Option<SurveyAnswerValue>): string[] {
  return pipe(
    answer,
    O.chain((value) => (value.kind === "choices" ? O.some(value.values) : O.none)),
    O.getOrElse((): string[] => []),
  )
}

function isSelected(answer: O.Option<SurveyAnswerValue>, expected: SurveyAnswerValue): boolean {
  return pipe(
    answer,
    O.exists((value) => JSON.stringify(value) === JSON.stringify(expected)),
  )
}

/** Boutons d'un choix unique (échelle, oui/non, option) : un seul sélectionné. */
function ChoiceButtons({
  label,
  choices,
  answer,
  onAnswer,
}: {
  label: string
  choices: ReadonlyArray<{ key: string; label: string; value: SurveyAnswerValue; dataTest: string }>
  answer: O.Option<SurveyAnswerValue>
  onAnswer: (value: SurveyAnswerValue) => void
}) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
      {choices.map((choice) => {
        const selected = isSelected(answer, choice.value)
        return (
          <Button
            key={choice.key}
            type="button"
            size="sm"
            variant={selected ? "default" : "outline"}
            role="radio"
            aria-checked={selected}
            onClick={() => onAnswer(choice.value)}
            data-test={choice.dataTest}
          >
            {choice.label}
          </Button>
        )
      })}
    </div>
  )
}

function QuestionInput({
  inputId,
  question,
  answer,
  onAnswer,
}: {
  inputId: string
  question: PublicSurveyQuestion
  answer: O.Option<SurveyAnswerValue>
  onAnswer: (value: SurveyAnswerValue) => void
}) {
  const scale = scalePoints(question.question_type_code)
  if (O.isSome(scale)) {
    return (
      <ChoiceButtons
        label={question.question_text}
        answer={answer}
        onAnswer={onAnswer}
        choices={scale.value.map((point) => ({
          key: String(point.value),
          label: point.label,
          value: { kind: "integer", value: point.value },
          dataTest: `${inputId}-option-${point.value}`,
        }))}
      />
    )
  }
  switch (question.question_type_code) {
    case "text_short":
    case "text_long": {
      const short = question.question_type_code === "text_short"
      const bounds = textLengthBounds(question)
      const text = textOf(answer)
      const changeText = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onAnswer({ kind: "text", value: event.target.value })
      const props = {
        id: inputId,
        "aria-label": question.question_text,
        value: text,
        minLength: bounds.min > 0 ? bounds.min : undefined,
        maxLength: bounds.max,
        "data-test": `${inputId}-text`,
      }
      return (
        <div className="space-y-1">
          {short ? (
            <Input {...props} onChange={changeText} />
          ) : (
            <Textarea {...props} rows={TEXT_LONG_ROWS} onChange={changeText} />
          )}
          <p className="text-right text-xs text-muted-foreground" data-test={`${inputId}-count`}>
            {bounds.min > 0 ? `${bounds.min} caractères au moins · ` : ""}
            {text.length} / {bounds.max}
          </p>
        </div>
      )
    }
    case "date":
      return (
        <Input
          id={inputId}
          type="date"
          aria-label={question.question_text}
          value={textOf(answer)}
          onChange={(event) => onAnswer({ kind: "text", value: event.target.value })}
          data-test={`${inputId}-date`}
        />
      )
    case "yes_no":
      return (
        <ChoiceButtons
          label={question.question_text}
          answer={answer}
          onAnswer={onAnswer}
          choices={[
            { key: "yes", label: "Oui", value: { kind: "boolean", value: true }, dataTest: `${inputId}-yes` },
            { key: "no", label: "Non", value: { kind: "boolean", value: false }, dataTest: `${inputId}-no` },
          ]}
        />
      )
    case "single_choice":
      return (
        <ChoiceButtons
          label={question.question_text}
          answer={answer}
          onAnswer={onAnswer}
          choices={question.options.map((option) => ({
            key: option.value,
            label: option.label,
            value: { kind: "text", value: option.value },
            dataTest: `${inputId}-choice-${option.value}`,
          }))}
        />
      )
    case "multiple_choice": {
      const chosen = choicesOf(answer)
      return (
        <div className="space-y-2">
          {question.options.map((option) => {
            const id = `${inputId}-${option.value}`
            return (
              <div key={option.value} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={chosen.includes(option.value)}
                  onCheckedChange={(checked) =>
                    onAnswer({
                      kind: "choices",
                      values: checked === true ? [...chosen, option.value] : chosen.filter((value) => value !== option.value),
                    })
                  }
                  data-test={`${inputId}-choice-${option.value}`}
                />
                <Label htmlFor={id} className="font-normal">
                  {option.label}
                </Label>
              </div>
            )
          })}
        </div>
      )
    }
    default:
      return (
        <p className="text-sm text-muted-foreground" data-test={`${inputId}-unsupported`}>
          Cette question ne se remplit pas en ligne : votre organisme de formation vous la posera autrement.
        </p>
      )
  }
}
